import React from 'react';
import PropTypes from 'prop-types';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Button from '../../../components/Button.js';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { prettyDate } from '../../../lib/time.js';
import { ProviderAPI } from '../../../api';
import Loader from '../../../components/Loader.js';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import { Alert, AlertTitle } from '@material-ui/lab';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    maxWidth: '9rem',
  },
  body: {
    fontSize: 14,
    maxWidth: '9rem',
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    cursor: 'pointer',
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.common.highlightBlue,
    },
  },
}))(TableRow);

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '0',
  },
  title: {
    marginBottom: '1rem',
  },
  controls: {
    padding: '1rem 0',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: '1rem',
  },
  activeNotice: {
    marginTop: '1rem',
    '& a': {
      textDecoration: 'none',
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
  },
}));

const ListingsSummary = ({providerUuid, selectListing, newListing}) => {
  const classes = useStyles();
  const [showActiveOnly, setShowActiveOnly] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [provider, setProvider] = React.useState();

  const toggleStatus = () => {
    setShowActiveOnly(!showActiveOnly);
  };

  const getData = async () => {
    try {
      const listings = await ProviderAPI.getListings(providerUuid, showActiveOnly);
      setData(listings.payload);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const getProvider = async () => {
    try {
      const response = await ProviderAPI.getProfile(providerUuid);
      setProvider(response.payload);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const selectChipColor = (status) => {
    if (status === 'active') {
      return 'lightGreen';
    } else if (status === 'closed') {
      return 'lightBlue';
    } else {
      return 'orange';
    }
  };

  const ActiveNotice = () => {
    if (!provider || !data) return null;
    const active = data.filter(listing => listing.status === 'active').length;
    const max = provider.max_listings;
    const severity = active < max ? 'success' : active === max ? 'info' : 'error';
    return (
      <Alert severity={severity} className={classes.activeNotice}>
        <AlertTitle>
          You have {Pluralize('active listings', active, true)}.
        </AlertTitle>
        Your account is eligible for {Pluralize('active listings', provider.max_listings, true)}.
        {' '}
        <Link to="/contact">Contact us</Link> to find out how to get more.
      </Alert>
    );
  };

  React.useEffect(() => {
    getData();
    getProvider();
  }, [showActiveOnly]);  // eslint-disable-line

  if (data === null) {
    return (<Loader />);
  }

  return (
    <div className={classes.root}>
      <div className={classes.controls}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showActiveOnly}
              onChange={toggleStatus}
            />
          }
          label="Show active listings only"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={newListing}
          label="New listing"
          className={classes.button}
          pointer="plus"
        />
      </div>
      <TableContainer>
        <Table className={classes.table} aria-label="Listings table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Title</StyledTableCell>
              <StyledTableCell>Service</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Active From</StyledTableCell>
              <StyledTableCell>Rating</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <StyledTableRow
                key={row.uuid}
                onClick={() => selectListing(row.uuid)}
              >
                <StyledTableCell>{row.title}</StyledTableCell>
                <StyledTableCell>{row.service}</StyledTableCell>
                <StyledTableCell><Chip label={row.status} style={{backgroundColor:selectChipColor(row.status)}} /></StyledTableCell>
                <StyledTableCell>{prettyDate(row.active_from)}</StyledTableCell>
                <StyledTableCell>{row.rating}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ActiveNotice/>
    </div>
  );
};

ListingsSummary.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  selectListing: PropTypes.func.isRequired,
  newListing: PropTypes.func.isRequired,
};

export default ListingsSummary;
